import { useRouter } from "next/navigation";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import { DataTable } from "@/components/DataTable";
import {
  Assistant,
  AssistantWithTags,
  AssistantsService,
  AttachFileSettings,
  KnowledgeBaseSelectorSettingsInput,
  NoxusQaSettingsInput,
  WebResearchSettingsInput,
} from "@/generated";
import { BotMessageSquare, CheckCheck, Plus, SearchX } from "lucide-react";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { DeploymentsStack } from "./DeploymentsStack";
import { DATA_KEYS, prefetchQuery, useRemoteData } from "@/utils/useRemoteData";
import { ChipFilter } from "../UI/ChipFilter";
import { useSpotUser } from "@/utils/spotUser";
import { useIsGenieEnabled } from "@/lib/genie/useIsGenieEnabled";
import {
  keepPreviousData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { OptionType } from "../Form/SelectField";
import { IDefinition } from "./AssistantDetailsContent";
import { useAssistantTableContextMenu } from "./useAssistantTableContextMenu";
import { SpotToast } from "../SpotToast";
import { getIcon } from "@/utils/assistants";
import { SearchBar } from "../SearchBar";
import WorkshopEmpty from "../Views/Workshop/WorkshopEmpty";
import { AssistantIconPicker } from "../Assistants/AssistantIconPicker";
import { AssistantDeleteDialog } from "../Modals/AssistantDeleteDialog";
import LoadingPageWorkshop from "../Views/Workshop/LoadingPageWorkshop";
import { timeAgo } from "@/utils/generic";
import { Badge } from "../shadcn/badge";
import { AssistantVersionActionDialog } from "../Dialogs/AssistantVersionActionDialog";
import AgentDetailsDialog from "../Dialogs/AgentDetailsDialog";
import { FlowTagsStack } from "../Tags/FlowTagsStack";
import { FlowTagActionsDialog, TagAction } from "../Tags/FlowTagActionsDialog";
import { FlowTagsMenu } from "../Tags/FlowTagsMenu";

const SizeOptions = [
  { label: "15 per page", value: "15" },
  { label: "20 per page", value: "20" },
  { label: "25 per page", value: "25" },
  { label: "30 per page", value: "30" },
];

const StatusOptions = [
  {
    label: "All",
    value: "all",
  },
  {
    label: "Live",
    value: "app_deployed",
  },
  {
    label: "Deleted",
    value: "deleted",
  },
];

export const AssistantsTable = ({
  defaultPageSize,
  ignoreIds,
  readOnly,
}: {
  defaultPageSize?: number;
  ignoreIds?: string[];
  readOnly?: boolean;
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [deleteModelOpen, setDeleteModelOpen] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(
    null
  );
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagAction, setTagAction] = useState<TagAction>();

  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<string | undefined>();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(StatusOptions[0]);
  const [versionActionDialog, setVersionActionDialog] = useState<{
    isOpen: boolean;
    action: "duplicate" | "export";
    assistant: Assistant | null;
  }>({ isOpen: false, action: "duplicate", assistant: null });

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const [pageSize, setPageSize] = useState(() => {
    if (defaultPageSize) {
      return defaultPageSize;
    }
    const height = window.innerHeight;
    if (height < 1050) {
      return Number(SizeOptions[0].value); // 15
    } else if (height < 1220) {
      return Number(SizeOptions[1].value); // 30
    } else {
      return Number(SizeOptions[2].value); // 45
    }
  }); // Default value

  /* Tags data */
  const { data: tagsData, isLoading: isTagsLoading } = useRemoteData(
    DATA_KEYS.TAGS,
    {
      path: { group_id: spotGroup.id },
    }
  );

  const isGenieEnabled = useIsGenieEnabled();

  /* Assistants data */
  const {
    data: assistantsData,
    isLoading,
    isPlaceholderData,
  } = useRemoteData(
    DATA_KEYS.ASSISTANTS,
    {
      path: { group_id: spotGroup.id },
      query: {
        page: currentPage,
        size: pageSize,
        query: searchValue || undefined,
        tags: selectedTags?.join(",") || undefined,
        status:
          selectedStatus.value === "all" ? undefined : selectedStatus.value,
        order_by: sortBy || undefined,
        sort: sortOrder || undefined,
        ignore_ids: ignoreIds?.join(",") || undefined,
        include_genie: isGenieEnabled || undefined,
      },
    },
    {
      placeholderData: keepPreviousData,
    }
  );

  const onSort = (sorting: SortingState) => {
    if (sorting.length > 0) {
      setSortBy(sorting[0].id);
      setSortOrder(sorting[0].desc ? "desc" : "asc");
    } else {
      setSortBy(undefined);
      setSortOrder(undefined);
    }
  };

  /* Memo */
  const getNoDataMessage = useMemo(() => {
    return searchValue !== "" && searchValue != undefined
      ? "The search retrieved no results."
      : selectedStatus.value === "deleted"
        ? "There are no deleted agent."
        : selectedStatus.value === "app_deployed"
          ? "There are no app deployed agents."
          : "There are no agents to display.";
  }, [searchValue, selectedStatus.value]);

  const restoreAssistantMutation = useMutation({
    mutationFn: AssistantsService.restoreAssistant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANTS] });
      SpotToast.success("Agent restored successfully");
    },
    onError: () => {
      SpotToast.error("Failed to restore assistant");
    },
  });

  const restoreAssistant = async (assistantId: string) => {
    restoreAssistantMutation.mutate({
      path: { group_id: spotGroup.id, assistant_id: assistantId },
    });
  };

  const openAssistant = (assistantId: string) => {
    if (assistantId) {
      window.open(`/assistant/${assistantId}`, "_blank", "noopener,noreferrer");
    } else {
      SpotToast.error("Could not open the chat. Please try again later.");
    }
  };

  const { getContextMenuItems } = useAssistantTableContextMenu({
    selectedStatus,
    openAssistant,
    setDeleteModelOpen,
    editAssistant: (assistant) => {
      setSelectedAssistant(assistant);
      setEditDialogOpen(true);
    },
    duplicateAssistant: (assistant) =>
      setVersionActionDialog({ isOpen: true, action: "duplicate", assistant }),
    restoreAssistant,
    exportAssistant: (assistant) =>
      setVersionActionDialog({ isOpen: true, action: "export", assistant }),
  });

  /* Columns */
  const columns: ColumnDef<AssistantWithTags, any>[] = useMemo(
    () => [
      {
        id: "name",
        accessorKey: "name",
        cell: ({ row }) => (
          <div className="flex gap-2 items-center">
            <div
              className="flex h-fit"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <AssistantIconPicker
                assistantId={row.original.id}
                icon={row.original?.image_url || "coworker"}
                size="sm"
              />
            </div>
            <div>
              <p className="font-base text-sm">{row.original.name}</p>
            </div>
            {row.original.app_settings_v2?.is_active && (
              <Badge variant="outline">Live</Badge>
            )}
          </div>
        ),
        header: "Name",
        sortDescFirst: true,
        meta: {
          isSortable: true,
          wrap: false,
          width: "30%",
        },
      },
      {
        id: "tags",
        cell: ({ row }) => (
          <div
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <FlowTagsStack
              onEditTag={(tag) => setTagAction({ type: "edit", tag })}
              onDeleteTag={(tag) => setTagAction({ type: "delete", tag })}
              entityId={row.original.id}
              entityType="assistant"
              entityTags={(row.original as any).tags || []}
              disabled={readOnly}
            />
          </div>
        ),
        header: "Tags",
        sortDescFirst: true,
        meta: {
          isSortable: false,
          wrap: false,
          width: "15%",
          noEllipsis: true,
        },
      },
      {
        id: "quick_actions",
        cell: ({ row }) =>
          getQuickActions(row.original.definition as unknown as IDefinition),
        header: "Actions",
        sortDescFirst: true,
        meta: {
          isSortable: false,
          wrap: false,
          width: "10%",
        },
      },
      {
        id: "deployments",
        cell: ({ row }) => (
          <DeploymentsStack deployments={row.original.deployments} />
        ),
        header: "Deployments",
        meta: {
          isSortable: false,
          wrap: false,
          width: "15%",
        },
      },
      {
        id: "last_updated_at",
        accessorKey: "last_updated_at",
        cell: ({ row }) => {
          return row.original.last_updated_at
            ? timeAgo(row.original.last_updated_at)
            : "-";
        },
        header: "Last modified",
        sortDescFirst: true,
        meta: {
          isSortable: true,
          wrap: false,
          width: "15%",
        },
      },
    ],
    [readOnly]
  );

  const getRowHref = (assistant: Assistant) => {
    return `/assistant/${assistant.id}`;
  };

  const handlePageSizeChange = (value: OptionType) => {
    setPageSize(Number(value.value));
  };

  const handleContextMenuOpen = (assistant: AssistantWithTags) => {
    setSelectedAssistant(assistant);
  };

  const isLoadingPage = isLoading || isTagsLoading;

  const hasSearch = searchValue !== "";
  const hasOtherFilters =
    selectedStatus.value !== "all" || selectedTags.length > 0;
  const hasActiveFilters = hasSearch || hasOtherFilters;

  const hasNone = assistantsData?.total === 0 && !hasActiveFilters;
  const allIgnored = hasNone && Boolean(ignoreIds?.length);
  const isEmpty = hasNone && !allIgnored;

  const clearFilters = () => {
    setSearchValue("");
    setSelectedStatus(StatusOptions[0]);
    setSelectedTags([]);
    setCurrentPage(1);
  };

  const noMatchDescription = hasSearch
    ? hasOtherFilters
      ? "Try a different search term or adjust your filters."
      : "Try a different search term."
    : "Try adjusting your filters.";

  return (
    <>
      <div className="flex justify-between gap-2 mb-4">
        <SearchBar
          placeholder="Search in this workspace"
          value={searchValue}
          setValue={handleSearchValueChange}
          isDebounced
        />

        <div className="flex gap-2">
          <ChipFilter
            selectedOrder={selectedStatus}
            orderSelectOptions={StatusOptions}
            setSelectedOrder={setSelectedStatus}
            labelIfFirstOption="Status"
            side="bottom"
            align="start"
          />
          <FlowTagsMenu
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            tags={tagsData || []}
          />
        </div>
      </div>

      {isLoadingPage && (
        <LoadingPageWorkshop pageSize={pageSize} columns={columns} />
      )}

      {!isLoadingPage && (
        <DataTable<AssistantWithTags>
          columns={columns}
          data={assistantsData?.items || []}
          isLoading={isLoading}
          loadingRows={pageSize}
          rowHref={getRowHref}
          noDataMessage={getNoDataMessage}
          onSort={onSort}
          contextMenuItems={readOnly ? undefined : getContextMenuItems}
          onContextMenuOpen={readOnly ? undefined : handleContextMenuOpen}
          noPrefetchLinks
          tableControls={{
            data: assistantsData,
            currentPage,
            setCurrentPage,
            pageSize,
            onPageSizeChange: handlePageSizeChange,
          }}
          fixedHeader
          emptyState={
            hasActiveFilters ? (
              <NoxusEmptyBlock
                size="sm"
                icon={SearchX}
                title={
                  hasSearch
                    ? "No agents match your search"
                    : "No agents match your filters"
                }
                description={noMatchDescription}
                button={
                  hasSearch && !hasOtherFilters
                    ? "Clear search"
                    : "Clear filters"
                }
                buttonVariant="outline"
                onClick={clearFilters}
              />
            ) : allIgnored ? (
              <NoxusEmptyBlock
                size="sm"
                icon={CheckCheck}
                title="All Agents are already open"
                description="Every Agent in this workspace is already open in a tab."
              />
            ) : isEmpty ? (
              <WorkshopEmpty
                toolId="co-worker"
                icon={BotMessageSquare}
                title="No Agents created yet"
                readerDescription="There are currently no Agents available for this group."
                button="Create Agent"
                buttonIcon={Plus}
                size="sm"
                description="Deploy AI agents that use flows, knowledge bases, and integrations to autonomously perform tasks."
              />
            ) : undefined
          }
        />
      )}

      {selectedAssistant && editDialogOpen && (
        <AgentDetailsDialog
          assistant={selectedAssistant}
          isOpen={editDialogOpen}
          onOpenChange={setEditDialogOpen}
        />
      )}
      {selectedAssistant && deleteModelOpen && (
        <AssistantDeleteDialog
          isOpen={deleteModelOpen}
          onOpenChange={setDeleteModelOpen}
          assistantId={selectedAssistant.id}
          assistantName={selectedAssistant.name}
          assistantIcon={selectedAssistant.image_url ?? undefined}
          isAppPublished={selectedAssistant.app_settings_v2?.is_active || false}
        />
      )}
      {versionActionDialog.assistant && versionActionDialog.isOpen && (
        <AssistantVersionActionDialog
          isOpen={versionActionDialog.isOpen}
          onOpenChange={(isOpen) =>
            setVersionActionDialog({ ...versionActionDialog, isOpen })
          }
          assistantId={versionActionDialog.assistant.id}
          assistantName={versionActionDialog.assistant.name}
          action={versionActionDialog.action}
        />
      )}
      <FlowTagActionsDialog
        action={tagAction}
        setAction={setTagAction}
        onDeleted={(tagId) => {
          setSelectedTags((current) => current.filter((id) => id !== tagId));
        }}
      />
    </>
  );
};

const getQuickActions = (definition: IDefinition) => {
  const quick_actions_tools = definition.tools.filter(
    (tool) =>
      tool.category === "QuickAction" &&
      tool.enabled &&
      tool.type !== "filesystem"
  ) as (
    | WebResearchSettingsInput
    | NoxusQaSettingsInput
    | KnowledgeBaseSelectorSettingsInput
    | AttachFileSettings
  )[];

  return (
    // <Tooltip label={label} placement="bottom-start">
    // </Tooltip>
    <div className="flex gap-1">
      {quick_actions_tools.map((tool) => {
        return (
          <div
            key={tool.type}
            className="flex items-center gap-1 text-primary-foreground px-1 rounded-md w-fit h-6"
            style={{
              backgroundColor: (tool as any).bg_color,
              color: (tool as any).icon_color,
            }}
          >
            {getIcon((tool as any).icon)}
          </div>
        );
      })}
    </div>
  );
};
